import { type CSSProperties, useState } from "react";
import RadioButtonBar, {
  orientationType,
} from "@/components/shared/RadioButtonBar";
import TransitionGroup from "react-transition-group/TransitionGroup";
import CSSTransition from "react-transition-group/CSSTransition";
import { createNodeRefs } from "@/common/nodeRefs";
import type { ExperienceState } from "@/types/state";

const REGION = "Favorite Image Picker";

type FavoriteImagePickerProps = {
  experienceState: ExperienceState;
  onFavoriteImageSelected?: (region: string, index: number) => void;
};

export default function FavoriteImagePicker({
  experienceState,
  onFavoriteImageSelected,
}: FavoriteImagePickerProps) {
  const [nodeRefFor] = useState(() => createNodeRefs<HTMLDivElement>());

  const onSetValue = (_name: string | undefined, index: number) => {
    if (onFavoriteImageSelected) {
      onFavoriteImageSelected(REGION, index);
    }
  };

  const metadata = experienceState.experienceData.metadata;
  const favoritesData = experienceState.favoritesData;
  const index = favoritesData.imageIndex;
  const favoriteIndex = favoritesData.favoriteIndex;
  const images = favoritesData.getFavoriteArticleImages();

  const imageElements = [];

  for (let n = 0; n < 3; n++) {
    const imageStyle: CSSProperties = {};

    if (images && images.length > n) {
      imageStyle.backgroundImage = `url(${metadata.getAssetUrl(images[n].filename)})`;
      imageElements.push(
        <button
          type="button"
          key={`favorite-image-${favoriteIndex}-${n}`}
          className="button hover-right tw:relative tw:grow tw:select-auto tw:appearance-none tw:break-inside-auto tw:text-inherit tw:[font:inherit] tw:[unicode-bidi:isolate]"
          aria-label={`Image ${n + 1}`}
          aria-pressed={n === index}
        >
          <div
            className="favorite-image tw:absolute tw:inset-[4px] tw:bg-center tw:bg-contain tw:bg-no-repeat"
            style={imageStyle}
          />
        </button>,
      );
    }
  }

  // The CSSTransition child must stay so the exiting bar keeps its image wrapper.
  const nodeRef = nodeRefFor(`fav-${favoriteIndex}`);

  return (
    <TransitionGroup
      component="div"
      className="favorite-image-picker tw:relative tw:grow tw:basis-0 tw:[.portrait_&]:hidden"
    >
      <CSSTransition
        key={`fav-${favoriteIndex}`}
        nodeRef={nodeRef}
        classNames="favorite-image"
        timeout={200}
      >
        <RadioButtonBar
          nodeRef={nodeRef}
          className="favorite-image-picker-bar"
          name="favorite"
          index={index}
          onSetValue={onSetValue}
          orientation={orientationType.PORTRAIT}
        >
          {imageElements}
        </RadioButtonBar>
      </CSSTransition>
    </TransitionGroup>
  );
}
